import { type GetListParams, type QueryFunctionContext } from "react-admin";

import { type components, createApiClient } from "@/api";
import { type DataProviderSchema } from "@/utils";
import { getLocalFilteredSortedList } from "@/utils/listParams";

import { type CampaignsListFilter } from "./types";

const apiClient = createApiClient("jsonld");
const BASE_URL = "/api/vpn/campaigns";

const fetchListData = async ({
  pagination,
  filter,
}: GetListParams & QueryFunctionContext) => {
  const { id, title, campaignId, deviceTypeId } = filter as CampaignsListFilter;
  const response = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        pagination: true,
        limit: pagination?.perPage,
        page: pagination?.page,
        id,
        title,
        campaignId,
        "deviceTypeId[]": deviceTypeId,
      },
    },
  });

  return {
    data: response.data?.["hydra:member"] ?? [],
    total: response.data?.["hydra:totalItems"] ?? 0,
  };
};

export const campaignsSchema: DataProviderSchema<"Campaign", "jsonld"> = {
  list: getLocalFilteredSortedList(fetchListData),
  detail: async ({ id }) => {
    const { data } = await apiClient.GET(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
    });

    if (!data) throw new Error("Campaign not found");

    return { data };
  },
  edit: async ({ id, data: { tds = [], ...body } }) => {
    const { data } = await apiClient.PUT(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
      body: {
        ...body,
        tds: (tds.length ? tds : undefined) as string[], // incorrect schema type
      } as components["schemas"]["Campaign:jsonld-campaign:write"],
    });

    if (!data) throw Error("Something went wrong");

    return { data };
  },
  create: async ({ data: { tds = [], ...body } }) => {
    const { data } = await apiClient.POST(BASE_URL, {
      body: {
        ...body,
        tds: (tds.length ? tds : undefined) as string[], // incorrect schema type
      } as components["schemas"]["Campaign:jsonld-campaign:write"],
    });

    if (!data) throw new Error("Something went wrong!");

    return {
      data,
    };
  },
};
